import { Component, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Group, LoadingOverlay, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

// English only, like the rest of the admin.
const translations = {
    en: {
        title: 'Something went wrong',
        'render-body': 'This page stopped with an error.',
        'chunk-body': 'Part of the dashboard could not be loaded. A new version may have just been released.',
        reload: 'Reload',
    },
};

/*
 * Why this exists. Every page is a lazy chunk with a content hash in its file
 * name, and a deploy replaces the whole build directory. A tab still running
 * the previous build asks for a chunk the deploy deleted, nginx answers
 * index.html, webpack rejects the import with ChunkLoadError, and React, with
 * nothing to catch it, unmounts the app: a white page.
 *
 * So a chunk that fails to load reloads the page once (the reload fetches the
 * new index.html and with it the new chunk names), and any other render error
 * shows a panel with a Reload button instead of a blank page.
 */

const CHUNK_MESSAGE =
    /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

/** A lazy import that could not be fetched: webpack's JS and CSS chunk errors, and the native-ESM wordings. */
export const isChunkLoadError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;
    const { name, code, message } = error as { name?: unknown; code?: unknown; message?: unknown };
    return (
        name === 'ChunkLoadError' ||
        code === 'CSS_CHUNK_LOAD_FAILED' ||
        (typeof message === 'string' && CHUNK_MESSAGE.test(message))
    );
};

// When this tab last reloaded itself. sessionStorage is per tab and survives
// the reload, which is exactly the memory the guard needs.
export const RELOAD_KEY = 'admin:chunk-reload-at';
// No second automatic reload inside this window: a chunk still missing after
// one reload is genuinely missing (a broken deploy), and reloading again would
// loop. Generous on purpose, since it must outlast the slowest boot; once it
// has passed, the next stale build in the same tab can heal itself again.
export const RELOAD_GUARD_MS = 5 * 60 * 1000;

/** Whether an automatic reload may happen now. Without storage there is no guard, so then never. */
export const mayAutoReload = (now = Date.now()): boolean => {
    // Offline, a reload would trade the panel for the browser's offline page.
    if (window.navigator.onLine === false) return false;
    try {
        const last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
        return Math.abs(now - last) >= RELOAD_GUARD_MS;
    } catch {
        return false;
    }
};

/** Reloads the page unless the guard refuses. The mark is written first, so the next load sees it. */
export const reloadOnce = (now = Date.now()): boolean => {
    if (!mayAutoReload(now)) return false;
    try {
        window.sessionStorage.setItem(RELOAD_KEY, String(now));
    } catch {
        return false;
    }
    window.location.reload();
    return true;
};

const detailOf = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return typeof error === 'string' ? error : '';
};

function ErrorPanel({ chunk, detail }: { chunk: boolean; detail: string }) {
    const t = translations.en;
    return (
        <Paper withBorder p="xl" radius="md" maw={480} mx="auto" mt="xl" role="alert">
            <Stack gap="sm" align="flex-start">
                <Group gap="xs" wrap="nowrap">
                    <IconAlertTriangle size={20} color="var(--mantine-color-orange-6)" />
                    <Title order={4}>{t.title}</Title>
                </Group>
                <Text size="sm" c="dimmed">
                    {chunk ? t['chunk-body'] : t['render-body']}
                </Text>
                {detail && (
                    <Text size="xs" c="dimmed" ff="monospace" lineClamp={3}>
                        {detail}
                    </Text>
                )}
                <Button leftSection={<IconRefresh size={16} />} onClick={() => window.location.reload()}>
                    {t.reload}
                </Button>
            </Stack>
        </Paper>
    );
}

interface BoundaryProps {
    children: ReactNode;
    /** A new value clears an error already on screen and renders the children again. */
    resetKey: string;
}

interface BoundaryState {
    failed: boolean;
    error: unknown;
    /** A chunk error that is reloading the page: show the loading veil, not the panel. */
    reloading: boolean;
}

const cleared: BoundaryState = { failed: false, error: null, reloading: false };

class Boundary extends Component<BoundaryProps, BoundaryState> {
    constructor(props: BoundaryProps) {
        super(props);
        this.state = cleared;
    }

    static getDerivedStateFromError(error: unknown): BoundaryState {
        // Render phase, so it only reads. Knowing here that a reload is coming
        // lets the veil render instead of the panel flashing up first.
        return { failed: true, error, reloading: isChunkLoadError(error) && mayAutoReload() };
    }

    componentDidCatch(error: unknown) {
        if (isChunkLoadError(error) && reloadOnce()) return;
        // The guard (or the storage) said no: the panel, not an endless veil.
        if (this.state.reloading) this.setState({ reloading: false });
    }

    componentDidUpdate(prevProps: BoundaryProps, prevState: BoundaryState) {
        // Only an error that was already on screen is cleared by a new key. The
        // navigation that CAUSED an error changes the key in the same update,
        // and clearing that error would only throw it again.
        if (this.state.failed && prevState.failed && prevProps.resetKey !== this.props.resetKey) {
            this.setState(cleared);
        }
    }

    render() {
        const { failed, error, reloading } = this.state;
        if (!failed) return this.props.children;
        if (reloading) return <LoadingOverlay visible zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />;
        return <ErrorPanel chunk={isChunkLoadError(error)} detail={detailOf(error)} />;
    }
}

/**
 * Wraps routed content (App.tsx: the login, partner and Layout chunks; Layout.tsx:
 * every page, header tabs and their nested routes alike). Changing the path
 * clears an error on screen, so another tab still opens after one page failed.
 */
export default function ErrorBoundary({ children }: { children: ReactNode }) {
    const location = useLocation();
    return <Boundary resetKey={location.pathname}>{children}</Boundary>;
}
